import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, DollarSign, RefreshCw, AlertCircle, Shield } from 'lucide-react';
import SEOHead from './SEOHead';
import Footer from './Footer';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8">
      <SEOHead
        title="Terms & Conditions"
        description="Nerd Nation terms and conditions — your rights and responsibilities as a student user of our AI-powered academic platform."
        path="/terms-and-conditions"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <FileText className="h-7 w-7" />
              Terms & Conditions
            </CardTitle>
            <p className="text-blue-100 text-sm mt-2">Nerd Nation - You Can Achieve Any Ambition With Us !</p>
          </CardHeader>
          <CardContent className="p-6 sm:p-8 space-y-8">
          {/* Payments Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Payments</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-gray-700 leading-relaxed">
                We do not accept cash from customers. All payments must be processed through our secure online payment system.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-yellow-800 mb-1">Important Note:</p>
                    <p className="text-yellow-700 text-sm">
                      We request customers not to pay in cash. If any employee requests cash payment, 
                      please inform the management immediately at{' '}
                      <a href="mailto:support@nerdindians.com" className="underline font-medium">
                        support@nerdindians.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Policy Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <RefreshCw className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Refund Policy</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-gray-700 leading-relaxed">
                We do not offer refunds on our products or services. We ensure that all students are fully 
                educated on the benefits and features of our courses before accepting payment.
              </p>
              <p className="text-gray-700 leading-relaxed">
                While we may inform students of any changes to the course schedule, such changes do not 
                qualify for any refund.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <p className="text-blue-800 text-sm">
                  <span className="font-semibold">Policy Rationale:</span> Our team provides comprehensive 
                  information about all courses, features, and benefits before enrollment to ensure you make 
                  an informed decision.
                </p>
              </div>
            </div>
          </div>

          {/* Complaints Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Complaints</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-gray-700 leading-relaxed">
                If you face any issues with our products or services, we are here to help. Please report 
                your concerns to our support team.
              </p>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600 text-sm mb-2">Contact Support:</p>
                <a 
                  href="mailto:support@nerdindians.com"
                  className="text-blue-600 font-semibold text-lg hover:underline flex items-center gap-2"
                >
                  <span>📧</span>
                  support@nerdindians.com
                </a>
              </div>
              <p className="text-gray-600 text-sm">
                Our team will respond to your complaint within 24-48 hours and work to resolve your issue promptly.
              </p>
            </div>
          </div>

          {/* Privacy Policy Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-gray-700 leading-relaxed">
                Your data security is our top priority. All customer data is encrypted using industry-standard 
                security protocols.
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div className="h-2 w-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">
                    Your personal information will <strong>not be shared</strong> with any third parties 
                    outside our company
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="h-2 w-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">
                    Customer data will be used only for remarketing of our products/services and those of 
                    our trusted partners
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="h-2 w-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">
                    Only authorized employees have access to customer data for legitimate business purposes
                  </p>
                </div>
              </div>
              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded mt-4">
                <p className="text-purple-800 text-sm">
                  <span className="font-semibold">Your Rights:</span> You have the right to request access to, 
                  correction of, or deletion of your personal data at any time by contacting our support team.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t pt-6">
            <p className="text-gray-500 text-sm text-center">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-gray-500 text-sm text-center mt-2">
              By using Nerd Nation services, you agree to these Terms & Conditions
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
